/**
 * web/src/hooks/mutations/useRevertTracker.ts
 * Reverts the agent's Sponsorship Tracker changes for a workflow step by
 * restoring the pre-image snapshot the step captured before its write.
 * Surfaced as the Revert button on the supplementary-review gate's
 * "Agent's Tracker changes" panel.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys';
import { toast } from 'sonner';
import { revertTrackerChanges } from '../../api/workflows';
import { toastApiError } from '../../lib/api';

/**
 * Restores the Tracker row cells to their pre-agent values.
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP POST; writes the pre-image back to the live Sheet;
 *              invalidates the workflow detail so the panel shows "reverted"
 * @idempotent yes — a second revert writes the same pre-image again
 */
export function useRevertTracker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { workflowId: string; stepId: string }) =>
      revertTrackerChanges(vars.workflowId, vars.stepId),
    onSuccess: (data, vars) => {
      toast.success(
        `Tracker row ${data.rowNumber} restored to its pre-agent values (${data.revertedColumns.length} columns)`,
      );
      qc.invalidateQueries({ queryKey: qk.workflow.root(vars.workflowId) });
    },
    onError: (err) => {
      toastApiError(err, 'Revert failed');
    },
  });
}
